import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  useGetSettingsQuery,
  useUpdateSettingsMutation,
  useGetUserIpsQuery,
  useAddUserIpMutation,
  useUpdateUserIpMutation,
  useUpdateUserIpStatusMutation,
  useGetCompanyDetailsQuery,
  useUpdateCompanyDetailsMutation,
} from 'redux/features/settings/settingsApi';
import { useGetDepartmentsQuery, useDeleteDepartmentMutation } from 'redux/features/departmentApi';
import {
  useAddTaxMutation,
  useGetTaxesQuery,
  useUpdateTaxMutation,
  useDeleteTaxMutation,
} from 'redux/features/taxApi';
import {
  useDeleteDesignationMutation,
  useGetDesignationsQuery,
} from 'redux/features/designationApi';

import Button from 'components/ui/Button';
import useUser from 'hooks/useUser';
import { formatHoursMinutes } from 'utils/timeTracker';
import ConfirmationModal from 'components/ui/ConfirmationModal';
import Switch from 'components/ui/Switch';
import SkeletonWrapper from 'components/ui/SkeletonWrapper';
import dayjs from 'lib/dayjs';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import CommandPalette from '../../components/ui/CommandPalette';
import AddDepartmentModal from './components/AddDepartmentModal';
import AddDesignationModal from './components/AddDesignationModal';
import EditDepartmentModal from './components/EditDepartmentModal';
import EditDesignationModal from './components/EditDesignationModal';
import UserIpModal from './components/UserIpModal';
import IpLogModal from './components/IpLogModal';
import CompanyDetails from './components/CompanyDetails';
import WorkSchedule from './components/WorkSchedule';

const AdminSettings = () => {
  const user = useUser();
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const perms = user?.permissions || {};

  // Check if user has permission for specific modules
  const canManageIp = isAdmin || perms.ipManagement !== 'none';
  const canWriteIp = isAdmin || perms.ipManagement === 'write';
  const canManageDept = isAdmin || perms.departmentManagement !== 'none';
  const canWriteDept = isAdmin || perms.departmentManagement === 'write';

  const [departments, setDepartments] = useState([]);
  const [expandedDepartment, setExpandedDepartment] = useState(null);
  const [salaryCalculationType, setSalaryCalculationType] = useState('WORKING_HOURS');
  const [forceCheckoutAllow, setForceCheckoutAllow] = useState(false);
  const [isFamilyDetailsUpdateAllow, setIsFamilyDetailsUpdateAllow] = useState(false);
  const [isLogUpdateAllow, setIsLogUpdateAllow] = useState(false);

  const [adminSettings, setAdminSettings] = useState({
    fullDayWorkingHours: 0,
    halfDayWorkingHours: 0,
    breakStartTime: '',
    breakEndTime: '',
    breakDuration: 0,
    fullDayTotalHours: 0,
    defaultWorkingHours: {
      hours: 8,
      minutes: 0,
      othours: 0,
      otminutes: 0,
    },
  });
  const [taxEntries, setTaxEntries] = useState([]);
  const [editingTaxId, setEditingTaxId] = useState(null);
  const [isNewTax, setIsNewTax] = useState({});

  const {
    data: departmentsData,
    isLoading: loadingDepartments,
    refetch: refetchDepartments,
  } = useGetDepartmentsQuery({}, { skip: !canManageDept });
  const [deleteDepartment] = useDeleteDepartmentMutation();

  // Designation API hooks
  const {
    data: designationsData,
    isLoading: loadingDesignations,
    refetch: refetchDesignations,
  } = useGetDesignationsQuery({}, { skip: !canManageDept });
  const [deleteDesignation] = useDeleteDesignationMutation();

  // Tax API hooks
  const [addTax, { isLoading: isAddingTax }] = useAddTaxMutation();
  const {
    data: taxEntriesList,
    isLoading: isLoadingTaxes,
    refetch: refetchTaxes,
  } = useGetTaxesQuery(undefined, { skip: !isAdmin });
  const [updateTax, { isLoading: isUpdatingTax }] = useUpdateTaxMutation();
  const [deleteTax, { isLoading: isDeletingTax }] = useDeleteTaxMutation();

  // Modal states
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [showAddDesignationModal, setShowAddDesignationModal] = useState(false);
  const [showEditDepartmentModal, setShowEditDepartmentModal] = useState(false);
  const [showEditDesignationModal, setShowEditDesignationModal] = useState(false);
  const [showIpLogModal, setShowIpLogModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedDesignation, setSelectedDesignation] = useState(null);
  const { data, isLoading } = useGetSettingsQuery(undefined, { skip: !isAdmin });
  const [updateSettings, { isLoading: isUpdating }] = useUpdateSettingsMutation();

  // Company Details hooks
  const { data: companyData, isLoading: isLoadingCompany } = useGetCompanyDetailsQuery(undefined, {
    skip: !isAdmin,
  });
  const [updateCompanyDetails, { isLoading: isUpdatingCompany }] =
    useUpdateCompanyDetailsMutation();

  // User IP Management hooks
  const {
    data: userIpsData,
    isLoading: isLoadingUserIps,
    refetch: refetchUserIps,
  } = useGetUserIpsQuery(undefined, { skip: !canManageIp });
  const [addUserIp, { isLoading: isAddingUserIp }] = useAddUserIpMutation();
  const [showIpActive, setShowIpActive] = useState(true);
  const [updateUserIp, { isLoading: isUpdatingUserIp }] = useUpdateUserIpMutation();
  const [updateUserIpStatus, { isLoading: isUpdatingIpStatus }] = useUpdateUserIpStatusMutation();
  const handleSaveCompanyDetails = async (payload) => {
    try {
      await updateCompanyDetails(payload).unwrap();
      toast.success('Company details updated successfully!');
    } catch (error) {
      console.error('Error updating company details:', error);
    }
  };

  const [showUserIpModal, setShowUserIpModal] = useState(false);
  const [selectedUserIp, setSelectedUserIp] = useState(null);
  const [showIpToggleConfirm, setShowIpToggleConfirm] = useState(false);
  const [ipToToggle, setIpToToggle] = useState(null);

  // Update adminSettings when API data is available
  useEffect(() => {
    if (data?.data) {
      setSalaryCalculationType(data.data.salary_calculation_type || 'WORKING_HOURS');
      setForceCheckoutAllow(data.data.is_fc_allow || false);
      setIsLogUpdateAllow(data.data.is_log_update_allow || false);
      setIsFamilyDetailsUpdateAllow(data.data.is_update_family_detail_allow || false);
      setAdminSettings({
        fullDayWorkingHours: data.data.full_day_working_hours || 8,
        halfDayWorkingHours: data.data.half_day_working_hours || 4,
        fullDayTotalHours: data.data.full_day_total_hours || 0,
        breakHours: data.data.break_hours || 0,
        defaultWorkingHours: {
          hours: data.data.default_working_hours || 8,
          minutes: data.data.default_working_minutes || 0,
          othours: data.data.default_overtime_hours || 0,
          otminutes: data.data.default_overtime_minutes || 0,
        },
        id: data.data.id,
        created_at: data.data.created_at,
        updated_at: data.data.updated_at,
      });
    }
  }, [data]);

  const loadDepartmentsAndDesignations = useCallback(() => {
    if (!departmentsData || !designationsData) return;

    // Process departments with designation count
    const departmentsWithCount = departmentsData?.departments?.map((dept) => ({
      ...dept,
      designationCount:
        designationsData?.filter((des) => des?.department_id === dept?.id)?.length || 0,
    }));

    setDepartments(departmentsWithCount);
  }, [departmentsData, designationsData]);

  useEffect(() => {
    loadDepartmentsAndDesignations();
  }, [loadDepartmentsAndDesignations]);

  useEffect(() => {
    if (taxEntriesList?.length > 0 && !isLoadingTaxes) {
      const normalizedEntries = taxEntriesList.map((entry) => {
        const normalizedType = String(entry.type).toLowerCase();
        return {
          id: entry.id,
          title: entry.title || '',
          type:
            normalizedType === 'percentage' || normalizedType === 'fixed'
              ? normalizedType
              : 'percentage',
          value: entry.value || '',
          description: entry.description || '',
        };
      });
      setTaxEntries(normalizedEntries);
    }
  }, [taxEntriesList, isLoadingTaxes]);

  const handleAddTaxEntry = () => {
    const tempId = `temp_${Date.now()}`;
    const newEntry = {
      id: tempId,
      title: '',
      type: 'PERCENTAGE',
      value: '',
      description: '',
    };

    const updatedEntries = [...taxEntries, newEntry];
    setTaxEntries(updatedEntries);
    setIsNewTax({ ...isNewTax, [tempId]: true });
    setEditingTaxId(tempId);
  };

  const handleSaveTaxEntry = async (entryId) => {
    const entry = taxEntries.find((t) => t.id === entryId);
    if (!entry) return;

    // Basic validation
    if (!entry.title || entry.value === '') {
      toast.error('Please fill in all required fields before saving.');
      return;
    }

    try {
      const payload = {
        title: entry.title,
        type: entry.type.toUpperCase(),
        value: parseFloat(entry.value),
        description: entry.description || '',
      };

      if (isNewTax[entryId]) {
        const result = await addTax(payload).unwrap();

        setTaxEntries((prev) =>
          prev.map((t) => (t.id === entryId ? { ...result, id: result.id || result.data?.id } : t)),
        );

        const newIsNewTax = { ...isNewTax };
        delete newIsNewTax[entryId];
        setIsNewTax(newIsNewTax);

        toast.success('Tax entry added successfully!');
        refetchTaxes();
      } else {
        await updateTax({ id: entryId, ...payload }).unwrap();
        toast.success('Tax entry updated successfully!');
        refetchTaxes();
      }

      setEditingTaxId(null);
    } catch (error) {
      console.error('Error saving tax entry:', error);
    }
  };

  const hasTaxChanged = (entryId) => {
    if (isNewTax[entryId]) return true;

    const currentEntry = taxEntries.find((t) => t.id === entryId);
    const originalEntry = taxEntriesList?.find((t) => t.id === entryId);

    if (!currentEntry || !originalEntry) return false;

    return (
      currentEntry.title !== (originalEntry.title || '') ||
      currentEntry.description !== (originalEntry.description || '') ||
      currentEntry.type !== String(originalEntry.type).toLowerCase() ||
      Number(currentEntry.value) !== Number(originalEntry.value)
    );
  };

  const handleEditTaxEntry = (id, field, value) => {
    const updatedEntries = taxEntries.map((entry) => {
      if (field === 'type' && entry.type === 'percentage') {
        return { ...entry, [field]: value, value: '' };
      }
      if (field === 'type' && entry.type === 'fixed') {
        return { ...entry, [field]: value, value: '' };
      }
      return entry.id === id ? { ...entry, [field]: value } : entry;
    });
    setTaxEntries(updatedEntries);
  };
  const handleDeleteTaxEntry = async (id) => {
    if (window.confirm('Are you sure you want to delete this tax entry?')) {
      try {
        if (isNewTax[id]) {
          const updatedEntries = taxEntries.filter((entry) => entry.id !== id);
          setTaxEntries(updatedEntries);

          const newIsNewTax = { ...isNewTax };
          delete newIsNewTax[id];
          setIsNewTax(newIsNewTax);

          toast.success('Tax entry removed!');
        } else {
          await deleteTax(id).unwrap();

          const updatedEntries = taxEntries.filter((entry) => entry.id !== id);
          setTaxEntries(updatedEntries);

          toast.success('Tax entry deleted successfully!');
          refetchTaxes();
        }
      } catch (error) {
        console.error('Error deleting tax entry:', error);
      }
    }
  };

  const handleStartEdit = (id) => {
    setEditingTaxId(id);
  };

  const handleStopEdit = () => {
    if (editingTaxId && isNewTax[editingTaxId]) {
      const entry = taxEntries.find((t) => t.id === editingTaxId);
      if (!entry?.title && !entry?.value) {
        const updatedEntries = taxEntries.filter((entry) => entry.id !== editingTaxId);
        setTaxEntries(updatedEntries);

        const newIsNewTax = { ...isNewTax };
        delete newIsNewTax[editingTaxId];
        setIsNewTax(newIsNewTax);
      }
    }
    setEditingTaxId(null);
  };

  const handleEditFieldClick = (e) => {
    e.stopPropagation();
  };

  const handleRowClick = (entryId, e) => {
    if (editingTaxId === entryId && !e.target.closest('input') && !e.target.closest('button')) {
      handleStopEdit();
    }
  };

  const handleSaveWorkingHours = async (data) => {
    try {
      const payload = {
        salary_calculation_type: salaryCalculationType,
        is_fc_allow: forceCheckoutAllow,
        is_log_update_allow: isLogUpdateAllow,
        ...(data ? data : {}),
      };

      if (adminSettings.id) {
        payload.id = adminSettings.id;
      }

      await updateSettings({ payload: payload, id: adminSettings.id }).unwrap();

      // Update local state if partial data passed
      if (data?.salary_calculation_type) setSalaryCalculationType(data.salary_calculation_type);
      if (data?.is_fc_allow !== undefined) setForceCheckoutAllow(data.is_fc_allow);
      if (data?.is_log_update_allow !== undefined) setIsLogUpdateAllow(data.is_log_update_allow);
      if (data?.is_update_family_detail_allow !== undefined)
        setIsFamilyDetailsUpdateAllow(data.is_update_family_detail_allow);

      toast.success('Settings updated successfully!');
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleDepartmentAdded = () => {
    refetchDepartments();
    loadDepartmentsAndDesignations();
  };

  const handleDepartmentUpdated = () => {
    refetchDepartments();
    loadDepartmentsAndDesignations();
  };

  const handleDesignationAdded = () => {
    refetchDesignations();
    loadDepartmentsAndDesignations();
  };

  const handleDesignationUpdated = () => {
    refetchDesignations();
    loadDepartmentsAndDesignations();
  };

  const handleEditDepartment = (department) => {
    const designations = designationsData?.filter((des) => des?.departmentId === department?.id);
    setSelectedDepartment({ ...department, designations: designations });
    setShowEditDepartmentModal(true);
  };

  const handleDeleteDepartment = async (department) => {
    const relatedDesignations = designationsData?.filter(
      (des) => des?.departmentId === department?.id,
    );

    if (relatedDesignations?.length > 0) {
      alert(
        `Cannot delete department "${department?.name}" because it has ${relatedDesignations?.length} designation(s). Please remove all designations first.`,
      );
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the department "${department?.name}"?`)) {
      return;
    }

    try {
      await deleteDepartment(department.id).unwrap();

      toast.success(`Department "${department?.name}" deleted successfully!`);
      refetchDepartments();
    } catch (error) {
      console.error('Error deleting department:', error);
    }
  };

  const handleAddDesignation = (departmentId) => {
    setSelectedDepartment(departments?.find((dept) => dept?.id === departmentId));
    setShowAddDesignationModal(true);
  };

  const handleEditDesignation = (designation) => {
    const department = departments?.find((dept) => dept?.id === designation?.department_id);
    setSelectedDesignation({ ...designation, department: department });
    setShowEditDesignationModal(true);
  };

  const handleDeleteDesignation = async (designation) => {
    if (
      !window.confirm(`Are you sure you want to delete the designation "${designation?.name}"?`)
    ) {
      return;
    }

    try {
      await deleteDesignation(designation.id).unwrap();

      toast.success(`Designation "${designation?.name}" deleted successfully!`);
      refetchDesignations();
      loadDepartmentsAndDesignations();
    } catch (error) {
      console.error('Error deleting designation:', error);
    }
  };

  const toggleDepartmentExpanded = (departmentId) => {
    setExpandedDepartment(expandedDepartment === departmentId ? null : departmentId);
  };

  // User IP Management handlers
  const handleAddUserIp = () => {
    setSelectedUserIp(null);
    setShowUserIpModal(true);
  };

  const handleEditUserIp = (ip) => {
    setSelectedUserIp(ip);
    setShowUserIpModal(true);
  };

  const handleSaveUserIp = async (payload) => {
    try {
      if (selectedUserIp) {
        // Update existing IP
        await updateUserIp({ id: selectedUserIp.id, payload }).unwrap();
        toast.success('IP address updated successfully!');
      } else {
        // Add new IP
        await addUserIp({ ...payload, is_active: true }).unwrap();
        toast.success('IP address added successfully!');
      }
      refetchUserIps();
    } catch (error) {
      console.error('Error saving IP:', error);
    }
  };

  const handleToggleIpStatus = (ip) => {
    setIpToToggle(ip);
    setShowIpToggleConfirm(true);
  };

  const confirmToggleStatus = async () => {
    if (!ipToToggle) return;
    try {
      await updateUserIpStatus({ id: ipToToggle.id, is_active: !ipToToggle.is_active }).unwrap();
      toast.success(
        `IP address ${!ipToToggle.is_active ? 'activated' : 'deactivated'} successfully!`,
      );
      refetchUserIps();
    } catch (error) {
      console.error('Error updating IP status:', error);
    } finally {
      setShowIpToggleConfirm(false);
      setIpToToggle(null);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar />
      <CommandPalette />
      <main className="lg:ml-60 pt-16">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Page Header */}
          <motion.div className="mb-8" variants={itemVariants}>
            <h1 className="text-2xl lg:text-[26px] font-bold text-gray-900 mb-2">Settings</h1>
            <p className="text-gray-600">
              Manage departments, designations,Work Schedule and system settings
            </p>
          </motion.div>
          {isAdmin && (
            <motion.div variants={itemVariants}>
              <SkeletonWrapper
                isLoading={isLoadingCompany}
                width="w-full"
                height="h-[240px]"
                className="mb-6 rounded-lg"
              >
                <CompanyDetails
                  data={companyData}
                  onSave={handleSaveCompanyDetails}
                  isLoading={isUpdatingCompany}
                />
              </SkeletonWrapper>
            </motion.div>
          )}

          {isAdmin && (
            <motion.div variants={itemVariants}>
              <SkeletonWrapper
                isLoading={isLoading}
                width="w-full"
                height="h-[180px]"
                className="mb-6 rounded-lg"
              >
                <WorkSchedule
                  onSave={handleSaveWorkingHours}
                  data={data?.data}
                  isLoading={isUpdating}
                />
              </SkeletonWrapper>
            </motion.div>
          )}

          {/* General Settings Section */}
          {isAdmin && (
            <SkeletonWrapper
              isLoading={isLoading}
              width="w-full"
              height="h-[600px]"
              className="my-9 rounded-lg"
            >
              <motion.div
                className="bg-white rounded-lg shadow border my-9 border-gray-200"
                variants={itemVariants}
              >
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <Icon name="Settings" size={24} className="text-gray-700" />
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">General Settings</h2>
                      <p className="text-sm text-gray-500 mt-0.5">Configure system-wide settings</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">
                      Salary Calculation Method
                    </h3>
                    <div className="space-y-3">
                      <label className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-400 has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50 transition-all">
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="salaryCalculationTime"
                            value="WORKING_HOURS"
                            className="w-4 h-4 text-blue-600"
                            checked={salaryCalculationType === 'WORKING_HOURS'}
                            onChange={() =>
                              handleSaveWorkingHours({ salary_calculation_type: 'WORKING_HOURS' })
                            }
                          />
                          <div>
                            <p className="font-medium text-gray-900">Full Day Working Hours</p>
                            <p className="text-sm text-gray-500">Excluding breaks</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600">
                            {formatHoursMinutes(adminSettings?.fullDayWorkingHours)}
                          </p>
                          <p className="text-xs text-gray-500">hours</p>
                        </div>
                      </label>

                      <label className="flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-blue-400 has-[:checked]:border-blue-500 has-[:checked]:bg-blue-50 transition-all">
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="salaryCalculationTime"
                            value="FULL_DAY_HOURS"
                            className="w-4 h-4 text-blue-600"
                            checked={salaryCalculationType === 'FULL_DAY_HOURS'}
                            onChange={() =>
                              handleSaveWorkingHours({ salary_calculation_type: 'FULL_DAY_HOURS' })
                            }
                          />
                          <div>
                            <p className="font-medium text-gray-900">Full Day Total Hours</p>
                            <p className="text-sm text-gray-500">Including breaks</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-blue-600">
                            {formatHoursMinutes(adminSettings?.fullDayTotalHours)}
                          </p>
                          <p className="text-xs text-gray-500">hours</p>
                        </div>
                      </label>
                    </div>

                    <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Icon name="Info" size={16} className="text-amber-600" />
                          <span className="text-sm font-medium text-amber-900">Break Hours</span>
                        </div>
                        <span className="text-lg font-bold text-amber-700">
                          {formatHoursMinutes(adminSettings?.breakHours)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3">
                    <h3 className="text-sm font-semibold text-gray-900  mb-3">
                      Employee Permissions
                    </h3>
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Allow Update Family Details
                          </label>
                          <p className="text-xs text-gray-500">
                            Enable or disable the update family details feature for employees
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            handleSaveWorkingHours({
                              is_update_family_detail_allow: !isFamilyDetailsUpdateAllow,
                            })
                          }
                          disabled={isUpdating}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isUpdating ? 'justify-center' : ''} ${isFamilyDetailsUpdateAllow ? 'bg-blue-600' : 'bg-gray-300'} ${isUpdating ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {isUpdating ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          ) : (
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isFamilyDetailsUpdateAllow ? 'translate-x-6' : 'translate-x-1'}`}
                            />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Allow Force Checkout Request
                          </label>
                          <p className="text-xs text-gray-500">
                            Allow employees to send a log update request for logs that were closed
                            by the automatic force checkout
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            handleSaveWorkingHours({ is_fc_allow: !forceCheckoutAllow })
                          }
                          disabled={isUpdating}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isUpdating ? 'justify-center' : ''} ${forceCheckoutAllow ? 'bg-blue-600' : 'bg-gray-300'} ${isUpdating ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {isUpdating ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          ) : (
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${forceCheckoutAllow ? 'translate-x-6' : 'translate-x-1'}`}
                            />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Allow Time Log Update Request
                          </label>
                          <p className="text-xs text-gray-500">
                            Enable or disable the request timelog update feature for employees
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            handleSaveWorkingHours({ is_log_update_allow: !isLogUpdateAllow })
                          }
                          disabled={isUpdating}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isUpdating ? 'justify-center' : ''} ${isLogUpdateAllow ? 'bg-blue-600' : 'bg-gray-300'} ${isUpdating ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {isUpdating ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                          ) : (
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isLogUpdateAllow ? 'translate-x-6' : 'translate-x-1'}`}
                            />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div>
                      {adminSettings?.updated_at && (
                        <p className="text-xs text-gray-500">
                          Last updated:{' '}
                          {dayjs(adminSettings.updated_at).format('D MMM YYYY, hh:mm A')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </SkeletonWrapper>
          )}

          {/* Tax Management Section */}
          {isAdmin && (
            <SkeletonWrapper
              isLoading={isLoadingTaxes}
              width="w-full"
              height="h-[400px]"
              className="mb-8 rounded-lg"
            >
              <motion.div className="bg-white rounded-lg shadow mb-8" variants={itemVariants}>
                <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div>
                      <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center space-x-2">
                        <Icon name="Calculator" size={20} className="sm:w-6 sm:h-6" />
                        <span>Deductions Management</span>
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Manage deductions entries and configurations
                      </p>
                    </div>
                    <button
                      onClick={handleAddTaxEntry}
                      disabled={isAddingTax || isUpdatingTax || isDeletingTax}
                      className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Icon name="Plus" size={16} />
                      <span>Add Deductions Entry</span>
                    </button>
                  </div>
                </div>

                <div className="p-4 sm:p-6">
                  {taxEntries.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <Icon name="Calculator" size={32} color="var(--color-secondary-300)" />
                      </div>
                      <p className="text-lg font-medium text-gray-900 mb-2">
                        No deductions entries found
                      </p>
                      <p className="text-sm text-gray-500 mb-6">
                        Create your first deductions configuration to get started
                      </p>
                      <button
                        onClick={handleAddTaxEntry}
                        className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200"
                      >
                        <Icon name="Plus" size={16} className="mr-2" />
                        Add Deductions Entry
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="hidden lg:block bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Title
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Type
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Value
                              </th>
                              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody
                            className="bg-white divide-y divide-gray-200"
                            onClick={(e) =>
                              editingTaxId && !e.target.closest('tr') && handleStopEdit()
                            }
                          >
                            {taxEntries.map((entry) => (
                              <tr
                                key={entry.id}
                                className="hover:bg-gray-50 transition-colors duration-150"
                              >
                                <td className="px-6 py-5">
                                  {editingTaxId === entry.id ? (
                                    <div className="space-y-2" onClick={handleEditFieldClick}>
                                      <input
                                        type="text"
                                        value={entry.title || ''}
                                        onChange={(e) =>
                                          handleEditTaxEntry(entry.id, 'title', e.target.value)
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        onKeyPress={(e) =>
                                          e.key === 'Enter' && handleSaveTaxEntry(entry.id)
                                        }
                                        autoFocus
                                        placeholder="Enter tax title"
                                      />
                                      <input
                                        type="text"
                                        value={entry.description || ''}
                                        onChange={(e) =>
                                          handleEditTaxEntry(
                                            entry.id,
                                            'description',
                                            e.target.value,
                                          )
                                        }
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="Enter tax description"
                                        onKeyPress={(e) =>
                                          e.key === 'Enter' && handleSaveTaxEntry(entry.id)
                                        }
                                      />
                                    </div>
                                  ) : (
                                    <div>
                                      <div className="text-sm font-semibold text-gray-900">
                                        {entry.title || 'Untitled Tax Entry'}
                                      </div>
                                      <div className="text-sm text-gray-500 mt-1">
                                        {entry.description || 'No description provided'}
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td className="px-6 py-5">
                                  <div className="flex space-x-6">
                                    <label className="flex items-center cursor-pointer">
                                      <input
                                        type="radio"
                                        value="percentage"
                                        disabled={editingTaxId !== entry.id}
                                        checked={entry.type === 'percentage'}
                                        onChange={(e) =>
                                          handleEditTaxEntry(entry.id, 'type', e.target.value)
                                        }
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                      />
                                      <span className="ml-2 text-sm font-medium text-gray-700">
                                        Percentage
                                      </span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                      <input
                                        type="radio"
                                        value="fixed"
                                        disabled={editingTaxId !== entry.id}
                                        checked={entry.type === 'fixed'}
                                        onChange={(e) =>
                                          handleEditTaxEntry(entry.id, 'type', e.target.value)
                                        }
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                      />
                                      <span className="ml-2 text-sm font-medium text-gray-700">
                                        Fixed Amount
                                      </span>
                                    </label>
                                  </div>
                                </td>
                                <td className="px-6 py-5">
                                  <div className="relative max-w-32">
                                    {entry.type === 'percentage' ? (
                                      <>
                                        <input
                                          type="number"
                                          disabled={editingTaxId !== entry.id}
                                          min="0"
                                          max={100}
                                          step="0.1"
                                          value={entry.value || ''}
                                          onChange={(e) =>
                                            handleEditTaxEntry(
                                              entry.id,
                                              'value',
                                              e.target.value > 100 ? 100 : e.target.value,
                                            )
                                          }
                                          className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                          placeholder="0.0"
                                        />
                                        <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 font-medium">
                                          %
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 font-medium">
                                          ₹
                                        </span>
                                        <input
                                          disabled={editingTaxId !== entry.id}
                                          type="number"
                                          min="0"
                                          step="1"
                                          value={entry.value || ''}
                                          onChange={(e) =>
                                            handleEditTaxEntry(entry.id, 'value', e.target.value)
                                          }
                                          className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                          placeholder="0"
                                        />
                                      </>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-5 text-right">
                                  <div className="flex items-center justify-end space-x-2">
                                    {editingTaxId === entry.id ? (
                                      <>
                                        <button
                                          onClick={() => handleSaveTaxEntry(entry.id)}
                                          disabled={
                                            isAddingTax || isUpdatingTax || !hasTaxChanged(entry.id)
                                          }
                                          className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-150 disabled:opacity-50"
                                          title="Save changes"
                                        >
                                          {(isAddingTax || isUpdatingTax) &&
                                          editingTaxId === entry.id ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                                          ) : (
                                            <Icon name="Check" size={16} />
                                          )}
                                        </button>
                                        <button
                                          onClick={handleStopEdit}
                                          className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-150"
                                          title="Cancel"
                                        >
                                          <Icon name="X" size={16} />
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <button
                                          onClick={() => handleStartEdit(entry.id)}
                                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                                          title="Edit tax entry"
                                        >
                                          <Icon name="Edit" size={16} />
                                        </button>
                                        <button
                                          onClick={() => handleDeleteTaxEntry(entry.id)}
                                          disabled={isDeletingTax}
                                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150 disabled:opacity-50"
                                          title="Delete tax entry"
                                        >
                                          <Icon name="Trash2" size={16} />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div
                        className="lg:hidden space-y-4"
                        onClick={(e) =>
                          editingTaxId && !e.target.closest('[data-card]') && handleStopEdit()
                        }
                      >
                        {taxEntries.map((entry) => (
                          <div
                            key={entry.id}
                            data-card
                            className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
                            onClick={(e) => handleRowClick(entry.id, e)}
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1 min-w-0">
                                {editingTaxId === entry.id ? (
                                  <div className="space-y-2" onClick={handleEditFieldClick}>
                                    <input
                                      type="text"
                                      value={entry.title || ''}
                                      onChange={(e) =>
                                        handleEditTaxEntry(entry.id, 'title', e.target.value)
                                      }
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                      onKeyPress={(e) =>
                                        e.key === 'Enter' && handleSaveTaxEntry(entry.id)
                                      }
                                      autoFocus
                                      placeholder="Enter tax title"
                                    />
                                    <input
                                      type="text"
                                      value={entry.description || ''}
                                      onChange={(e) =>
                                        handleEditTaxEntry(entry.id, 'description', e.target.value)
                                      }
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                      placeholder="Enter tax description"
                                      onKeyPress={(e) =>
                                        e.key === 'Enter' && handleSaveTaxEntry(entry.id)
                                      }
                                    />
                                  </div>
                                ) : (
                                  <div>
                                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                                      {entry.title || 'Untitled Tax Entry'}
                                    </h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                      {entry.description || 'No description provided'}
                                    </p>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center space-x-1 ml-4">
                                {editingTaxId === entry.id ? (
                                  <>
                                    <button
                                      onClick={() => handleSaveTaxEntry(entry.id)}
                                      disabled={
                                        isAddingTax || isUpdatingTax || !hasTaxChanged(entry.id)
                                      }
                                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-150"
                                      title="Save"
                                    >
                                      {(isAddingTax || isUpdatingTax) &&
                                      editingTaxId === entry.id ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                                      ) : (
                                        <Icon name="Check" size={16} />
                                      )}
                                    </button>
                                    <button
                                      onClick={handleStopEdit}
                                      className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors duration-150"
                                      title="Cancel"
                                    >
                                      <Icon name="X" size={16} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => handleStartEdit(entry.id)}
                                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150"
                                      title="Edit"
                                    >
                                      <Icon name="Edit" size={16} />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTaxEntry(entry.id)}
                                      disabled={isDeletingTax}
                                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150"
                                      title="Delete"
                                    >
                                      <Icon name="Trash2" size={16} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">
                                  Tax Type
                                </label>
                                <div className="flex space-x-4">
                                  <label className="flex items-center cursor-pointer">
                                    <input
                                      type="radio"
                                      value="percentage"
                                      checked={entry.type === 'percentage'}
                                      onChange={(e) =>
                                        handleEditTaxEntry(entry.id, 'type', e.target.value)
                                      }
                                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                      disabled={editingTaxId !== entry.id}
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">
                                      Percentage
                                    </span>
                                  </label>
                                  <label className="flex items-center cursor-pointer">
                                    <input
                                      type="radio"
                                      value="fixed"
                                      checked={entry.type === 'fixed'}
                                      onChange={(e) =>
                                        handleEditTaxEntry(entry.id, 'type', e.target.value)
                                      }
                                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                                      disabled={editingTaxId !== entry.id}
                                    />
                                    <span className="ml-2 text-sm font-medium text-gray-700">
                                      Fixed Amount
                                    </span>
                                  </label>
                                </div>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-2">
                                  Tax Value
                                </label>
                                <div className="relative max-w-32">
                                  {entry.type === 'percentage' ? (
                                    <>
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        value={entry.value || ''}
                                        onChange={(e) =>
                                          handleEditTaxEntry(entry.id, 'value', e.target.value)
                                        }
                                        className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="0.0"
                                        disabled={editingTaxId !== entry.id}
                                      />
                                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 font-medium">
                                        %
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500 font-medium">
                                        ₹
                                      </span>
                                      <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={entry.value || ''}
                                        onChange={(e) =>
                                          handleEditTaxEntry(entry.id, 'value', e.target.value)
                                        }
                                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                        placeholder="0"
                                        disabled={editingTaxId !== entry.id}
                                      />
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </SkeletonWrapper>
          )}

          {canManageDept && (
            <SkeletonWrapper
              isLoading={loadingDepartments || loadingDesignations}
              width="w-full"
              height="h-[400px]"
              className="mb-8 rounded-lg"
            >
              <motion.div className="bg-white rounded-lg shadow mb-8" variants={itemVariants}>
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                        <Icon name="Building" size={24} />
                        <span>Departments</span>
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Manage organizational departments and their designations
                      </p>
                    </div>
                    <button
                      onClick={() => setShowAddDepartmentModal(true)}
                      disabled={!canWriteDept}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Icon name="Plus" size={16} />
                      <span className="hidden sm:inline">Add Department</span>
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {departments?.length === 0 ? (
                    <div className="text-center py-12">
                      <Icon
                        name="Building"
                        size={48}
                        color="var(--color-secondary-300)"
                        className="mx-auto mb-4"
                      />
                      <p className="text-lg font-medium text-gray-500 mb-2">No departments found</p>
                      <p className="text-sm text-gray-400">
                        Click "Add Department" to create your first department
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {departments?.map((department) => (
                        <div key={department?.id} className="border border-gray-200 rounded-lg">
                          {/* Department Row */}
                          <div className="p-4 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors">
                            <div className="flex items-center space-x-4 flex-1">
                              <button
                                onClick={() => toggleDepartmentExpanded(department?.id)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                <Icon
                                  name={
                                    expandedDepartment === department?.id
                                      ? 'ChevronDown'
                                      : 'ChevronRight'
                                  }
                                  size={20}
                                />
                              </button>
                              <div className="flex-1">
                                <h3 className="font-medium capitalize text-gray-900">
                                  {department?.name}
                                </h3>
                              </div>
                              <div className="text-sm text-gray-600">
                                {department?.designationCount} designation
                                {department?.designationCount !== 1 ? 's' : ''}
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleEditDepartment(department)}
                                  disabled={!canWriteDept}
                                  className="text-blue-600 hover:text-blue-800 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Edit Department"
                                >
                                  <Icon name="Edit" size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteDepartment(department)}
                                  disabled={!canWriteDept}
                                  className="text-red-600 hover:text-red-800 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Delete Department"
                                >
                                  <Icon name="Trash2" size={16} />
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Designations Section - Expanded - Updated to use designationsData */}
                          {expandedDepartment === department?.id && (
                            <div className="border-t border-gray-200 bg-white">
                              <div className="p-4">
                                <div className="flex items-center justify-between mb-4">
                                  <h4 className="font-medium text-gray-700 flex items-center space-x-2">
                                    <Icon name="Users" size={18} />
                                    <span>Designations</span>
                                  </h4>
                                  <button
                                    disabled={!canWriteDept}
                                    onClick={() => handleAddDesignation(department?.id)}
                                    className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <Icon name="Plus" size={14} />
                                    <span>Add Designation</span>
                                  </button>
                                </div>

                                {designationsData?.filter(
                                  (des) => des?.department_id === department?.id,
                                )?.length === 0 ? (
                                  <div className="text-center py-8 text-gray-500">
                                    <Icon
                                      name="Users"
                                      size={32}
                                      color="var(--color-secondary-300)"
                                      className="mx-auto mb-2"
                                    />
                                    <p className="text-sm">No designations in this department</p>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {designationsData
                                      ?.filter((des) => des?.department_id === department?.id)
                                      ?.map((designation) => (
                                        <div
                                          key={designation?.id}
                                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                        >
                                          <span className="font-medium text-gray-800">
                                            {designation?.name}
                                          </span>
                                          <div className="flex items-center space-x-2">
                                            <button
                                              onClick={() => handleEditDesignation(designation)}
                                              disabled={!canWriteDept}
                                              className="text-blue-600 hover:text-blue-800 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                              title="Edit Designation"
                                            >
                                              <Icon name="Edit" size={14} />
                                            </button>
                                            <button
                                              onClick={() => handleDeleteDesignation(designation)}
                                              disabled={!canWriteDept}
                                              className="text-red-600 hover:text-red-800 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                              title="Delete Designation"
                                            >
                                              <Icon name="Trash2" size={14} />
                                            </button>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            </SkeletonWrapper>
          )}

          {/* User IP Management Section */}
          {canManageIp && (
            <SkeletonWrapper
              isLoading={isLoadingUserIps}
              width="w-full"
              height="h-[500px]"
              className="mb-8 rounded-lg"
            >
              <motion.div
                className="bg-white rounded-lg shadow mb-8 h-[500px] "
                variants={itemVariants}
              >
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                        <Icon name="Globe" size={24} />
                        <span>User IP Management</span>
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Manage allowed IP addresses for user access
                      </p>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex gap-3 justify-center items-center">
                        <span>{showIpActive ? 'Active' : 'Inactive'}</span>
                        <Switch
                          checked={showIpActive}
                          onChange={() => setShowIpActive((flag) => !flag)}
                        />
                      </div>

                      <Button
                        onClick={handleAddUserIp}
                        disabled={isAddingUserIp || isUpdatingUserIp || !canWriteIp}
                        className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        loading={isAddingUserIp || isUpdatingUserIp}
                      >
                        <Icon name="Plus" size={16} />
                        <span className="hidden sm:inline">Add IP Address</span>
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="p-6">
                  {(() => {
                    // userIpsData is already transformed to an array by transformResponse
                    const ipList = Array.isArray(userIpsData) ? userIpsData : [];

                    if (ipList.filter((ip) => ip.is_active === showIpActive).length === 0) {
                      return (
                        <div className="text-center py-12">
                          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <Icon name="Globe" size={32} color="var(--color-secondary-300)" />
                          </div>
                          <p className="text-lg font-medium text-gray-900 mb-2">
                            No {showIpActive ? 'Active' : 'Inactive'} IP addresses found
                          </p>
                          <p className="text-sm text-gray-500 mb-6">
                            Add your first IP address to get started
                          </p>
                          {showIpActive && (
                            <Button
                              onClick={handleAddUserIp}
                              disabled={!canWriteIp || isAddingUserIp || isUpdatingUserIp}
                              className="inline-flex disabled:opacity-50 disabled:cursor-not-allowed items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors duration-200"
                              loading={isAddingUserIp || isUpdatingUserIp}
                            >
                              <Icon name="Plus" size={16} className="mr-2" />
                              Add IP Address
                            </Button>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div className="overflow-x-auto h-[380px] ">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Name
                              </th>{' '}
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                IP Address
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Created At
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {ipList
                              .filter((ip) => ip.is_active === showIpActive)
                              .map((ip) => (
                                <tr
                                  key={ip.id}
                                  className="hover:bg-gray-50 transition-colors duration-150"
                                >
                                  <td className="px-6 py-4">
                                    <div className="text-sm  text-gray-900">{ip.name}</div>
                                    {ip.description && (
                                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">
                                        {ip.description}
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm font-mono text-gray-700 bg-gray-50 px-2 py-1 rounded border border-gray-100 inline-block">
                                      {ip.ip_address}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    {ip.is_restricted ? (
                                      <span
                                        className={`inline-flex  items-center px-3 py-1 rounded-full text-xs font-medium transition-colors bg-yellow-100 text-yellow-800 hover:bg-yellow-200
                                    disabled:opacity-50 disabled:cursor-not-allowed`}
                                      >
                                        <Icon name="OctagonAlert" size={12} className="mr-1" />
                                        Restricted
                                      </span>
                                    ) : (
                                      <button
                                        onClick={() => handleToggleIpStatus(ip)}
                                        disabled={
                                          isUpdatingIpStatus || !canWriteIp || ip.is_restricted
                                        }
                                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                          ip.is_active
                                            ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                            : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                      >
                                        {isUpdatingIpStatus && ip.id === ipToToggle?.id ? (
                                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current mr-1"></div>
                                        ) : (
                                          <Icon
                                            name={ip.is_active ? 'CheckCircle' : 'XCircle'}
                                            size={12}
                                            className="mr-1"
                                          />
                                        )}
                                        {ip.is_active ? 'Active' : 'Inactive'}
                                      </button>
                                    )}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-500">
                                      {ip.created_at
                                        ? new Date(ip.created_at).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                          })
                                        : 'N/A'}
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div className="flex items-center justify-end space-x-2">
                                      <button
                                        onClick={() => handleEditUserIp(ip)}
                                        disabled={
                                          isUpdatingUserIp || ip.is_restricted || !canWriteIp
                                        }
                                        className="p-2 text-gray-400 disabled:bg-gray-50 text-green-600 disabled:text-gray-600 bg-green-50 rounded-lg transition-colors duration-150 disabled:opacity-50"
                                        title="Edit IP address"
                                      >
                                        <Icon name="Edit" size={16} />
                                      </button>
                                      <button
                                        onClick={() => {
                                          setSelectedUserIp(ip);
                                          setShowIpLogModal(true);
                                        }}
                                        disabled={
                                          isUpdatingUserIp || ip.is_restricted || !canWriteIp
                                        }
                                        className="p-2 text-gray-400 disabled:text-gray-600 disabled:bg-gray-50 text-primary-600 bg-primary-50 rounded-lg transition-colors duration-150 disabled:opacity-50"
                                        title="View logs"
                                      >
                                        <Icon name="History" size={16} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              </motion.div>
            </SkeletonWrapper>
          )}
        </motion.div>
      </main>

      <IpLogModal
        isOpen={showIpLogModal}
        onClose={() => {
          setShowIpLogModal(false);
          setSelectedUserIp(null);
        }}
        userIpId={selectedUserIp?.id}
        user={selectedUserIp}
      />

      {/* Modals */}
      <AddDepartmentModal
        isOpen={showAddDepartmentModal}
        onClose={() => setShowAddDepartmentModal(false)}
        onDepartmentAdded={handleDepartmentAdded}
      />
      <AddDesignationModal
        isOpen={showAddDesignationModal}
        onClose={() => setShowAddDesignationModal(false)}
        department={selectedDepartment}
        onDesignationAdded={handleDesignationAdded}
      />
      <EditDepartmentModal
        isOpen={showEditDepartmentModal}
        onClose={() => setShowEditDepartmentModal(false)}
        department={selectedDepartment}
        onDepartmentUpdated={handleDepartmentUpdated}
      />
      <EditDesignationModal
        isOpen={showEditDesignationModal}
        onClose={() => setShowEditDesignationModal(false)}
        designation={selectedDesignation}
        onDesignationUpdated={handleDesignationUpdated}
      />
      <UserIpModal
        isOpen={showUserIpModal}
        onClose={() => {
          setShowUserIpModal(false);
          setSelectedUserIp(null);
        }}
        onSuccess={handleSaveUserIp}
        ipData={selectedUserIp}
        isLoading={isAddingUserIp || isUpdatingUserIp}
      />

      <ConfirmationModal
        isOpen={showIpToggleConfirm}
        onClose={() => {
          setShowIpToggleConfirm(false);
          setIpToToggle(null);
        }}
        onConfirm={confirmToggleStatus}
        title={ipToToggle?.is_active ? 'Deactivate IP Address' : 'Activate IP Address'}
        message={`Are you sure you want to ${
          ipToToggle?.is_active ? 'deactivate' : 'activate'
        } the IP address "${ipToToggle?.ip_address}"?`}
        isLoading={isUpdatingIpStatus}
      />
    </div>
  );
};

export default AdminSettings;
